import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, delay, EMPTY, mergeMap, Observable, of, retry, tap, throwError } from 'rxjs';
import { AccountCategory, AccountCategoryResponse } from 'src/app/models/api/accountManagement.model';
import { AccountManagementHttpService } from '../../httpServices/accountManagementHttpService/account-management-http-service.service';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import { NotificationsService } from '../notifications/notifications.service';
import { HttpErrorInterceptor } from 'src/app/interceptors/http-error/http-error.interceptor';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly _accountCategory$ = new BehaviorSubject<AccountCategory | null>(null);

  private readonly _accountHttpService = inject(AccountManagementHttpService);
  private readonly _userService = inject(UserService);
  private readonly _router = inject(Router);
  private readonly _notificationService = inject(NotificationsService);

  public getAccountCategory$(): Observable<AccountCategory | null> {
    return this._accountCategory$.asObservable();
  }

  public setAccountCategory(accountDetails: AccountCategory | null): void {
    this._accountCategory$.next(accountDetails);
  }

  public getAccountDetails$(): Observable<AccountCategoryResponse> {
    const maxRetryAttempts = 3;

    return this._accountHttpService.getAccountDetails$().pipe(
      mergeMap((resp) => {
        if (resp.category === AccountCategory.inactive) {
          this._logout();

          return EMPTY;
        }

        return of(resp);
      }),
      tap((resp) => this.setAccountCategory(resp.category ?? null)),
      retry({
        count: maxRetryAttempts - 1,
        delay: (error, retryCount) => {
          // Don't retry or logout on 403 - user has no access groups but should stay on page
          if (error.status === 403) {
            this.setAccountCategory(null);
            return throwError(() => error);
          }

          if (error.status >= 400) {
            if (retryCount < maxRetryAttempts - 1) {
              return of(error).pipe(delay(1000));
            }

            this._notificationService.error('Account category is not provided');
            this.setAccountCategory(null);
            this._logout();

            return throwError(() => error);
          }

          return throwError(() => error);
        },
      }),
      catchError(() => EMPTY)
    );
  }

  public accountHasFullAccess(accountCategory: AccountCategory | null): boolean {
    return (
      accountCategory === AccountCategory.internal ||
      accountCategory === AccountCategory.paid ||
      accountCategory === AccountCategory.zenity ||
      accountCategory === AccountCategory.poc
    );
  }

  private _logout(): void {
    if (HttpErrorInterceptor.hasAccessRestriction) {
      return;
    }
    this._userService.logout();
    this._router.navigateByUrl('/login');
  }
}
